import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import { isConflictError, safeArray, safeGet } from "@/utils/error-mapper";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import { COMMON_TEXTS } from "./messages";

const ITEM_CODES_SEPARATOR = ", ";

export const conflictsFromError = (error: unknown): CampaignConflictItem[] => {
  if (!isConflictError(error)) return [];
  return safeArray<CampaignConflictItem>(safeGet<unknown>(error, "data.conflicts", []));
};

export const isConcurrentChangeError = (error: unknown): boolean =>
  isConflictError(error) && !Array.isArray(safeGet<unknown>(error, "data.conflicts", undefined));

export const itemCodesFromError = (error: unknown): string[] =>
  safeArray<string>(safeGet<unknown>(error, "data.itemCodes", []));

const blockedItemCodes = (error: unknown): string[] =>
  safeArray<{ itemCode?: string }>(safeGet<unknown>(error, "data.blocked", [])).map(
    (issue: { itemCode?: string }) => issue.itemCode ?? "",
  );

const singleItemCode = (error: unknown): string[] => {
  const itemCode: unknown = safeGet<unknown>(error, "data.itemCode", undefined);
  return typeof itemCode === "string" ? [itemCode] : [];
};

const relatedItemCodes = (error: unknown): string[] => {
  const codes: string[] = [
    ...itemCodesFromError(error),
    ...safeArray<string>(safeGet<unknown>(error, "data.missingItemCodes", [])),
    ...blockedItemCodes(error),
    ...singleItemCode(error),
  ].filter((code: string) => code.length > 0);
  return [...new Set(codes)];
};

export const describeApiError = (error: unknown, fallback: string): string => {
  const message: string = extractRtkErrorMessage(error, fallback);
  const itemCodes: string[] = relatedItemCodes(error);
  if (itemCodes.length === 0) return message;
  return `${message} ${COMMON_TEXTS.ITEM_CODES_SUFFIX(itemCodes.join(ITEM_CODES_SEPARATOR))}`;
};
